/**
 * NaviFace — the visible face of NAVI across the platform.
 *
 * Renders the illustrated NAVI portrait (extracted from the canonical
 * "Navi Intro" asset) inside a circular frame with a subtle gold ring
 * that matches the Altara brand.
 *
 * Sizes:
 *   xs (20px), sm (28px), md (40px), lg (56px), xl (96px)
 *
 * Usage:
 *   <NaviFace size="sm" />
 *   <NaviFace size="md" pulse />   // gentle "listening" pulse
 */
const SRC_256 = "/navi/navi-bust-256.webp";
const SRC_128 = "/navi/navi-bust-128.webp";
const SRC_512 = "/navi/navi-bust-512.webp";
const SRC_FALLBACK = "/navi/navi-bust-256.png";

const GOLD = "#C9A24A";
const NAVY = "#0A1F44";

const SIZES = {
  xs: { px: 20, ring: 1.5, src: SRC_128 },
  sm: { px: 28, ring: 2,   src: SRC_128 },
  md: { px: 40, ring: 2.5, src: SRC_256 },
  lg: { px: 56, ring: 3,   src: SRC_256 },
  xl: { px: 96, ring: 3.5, src: SRC_512 },
};

export default function NaviFace({
  size = "md",
  pulse = false,
  ring = true,
  className = "",
  alt = "NAVI",
  onClick,
  ...rest
}) {
  const s = SIZES[size] || SIZES.md;
  const wrapStyle = {
    width: s.px,
    height: s.px,
    borderRadius: "9999px",
    background: NAVY,
    boxShadow: ring ? `0 0 0 ${s.ring}px ${GOLD}` : undefined,
    flex: "0 0 auto",
    position: "relative",
    overflow: "hidden",
    display: "inline-block",
    cursor: onClick ? "pointer" : undefined,
  };
  const imgStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  };
  return (
    <span
      className={`navi-face ${pulse ? "navi-face--pulse" : ""} ${className}`.trim()}
      style={wrapStyle}
      onClick={onClick}
      data-testid={rest["data-testid"] || "navi-face"}
      aria-label={alt}
      role={onClick ? "button" : "img"}
    >
      <picture>
        <source srcSet={s.src} type="image/webp" />
        <img src={SRC_FALLBACK} alt={alt} style={imgStyle} draggable={false} />
      </picture>
      <style>{`
        @keyframes navi-face-pulse {
          0%,100% { box-shadow: 0 0 0 ${s.ring}px ${GOLD}, 0 0 0 ${s.ring + 2}px ${GOLD}22; }
          50%     { box-shadow: 0 0 0 ${s.ring}px ${GOLD}, 0 0 0 ${s.ring + 10}px ${GOLD}00; }
        }
        .navi-face--pulse { animation: navi-face-pulse 2.2s ease-in-out infinite; }
      `}</style>
    </span>
  );
}
